import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { CheckIcon, CopyIcon } from "@app/lib/events-admin-icons";
import { buildEventLandingPageUrl } from "@app/lib/events-admin-utils";
import "./copy-landing-page-button.css";

const LABEL_IDLE = "Copy landing page URL to clipboard";
const LABEL_COPIED = "Copied";
const RESET_DELAY_MS = 2000;

type TooltipState = "hidden" | "hover" | "copied";

type CopyLandingPageButtonProps = {
  eventId: string;
  className?: string;
  iconClassName?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export function CopyLandingPageButton({
  eventId,
  className,
  iconClassName,
  onClick,
}: CopyLandingPageButtonProps) {
  const [tooltipState, setTooltipState] = useState<TooltipState>("hidden");
  const [tooltipText, setTooltipText] = useState(LABEL_IDLE);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const landingPageUrl = buildEventLandingPageUrl(eventId);
  const isDisabled = !landingPageUrl;

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!landingPageUrl) return;

      setTooltipText(LABEL_COPIED);
      setTooltipState("copied");

      try {
        await navigator.clipboard.writeText(landingPageUrl);
        if (resetTimer.current) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => {
          setTooltipState("hidden");
        }, RESET_DELAY_MS);
      } catch {
        setTooltipState("hidden");
      }
    },
    [landingPageUrl, onClick],
  );

  const tooltipVisible = tooltipState !== "hidden";
  const copied = tooltipState === "copied";

  return (
    <span
      className="copy-lp-tooltip-anchor"
      onMouseEnter={() => {
        setTooltipState((prev) => {
          if (prev === "hidden") {
            setTooltipText(LABEL_IDLE);
            return "hover";
          }

          return prev;
        });
      }}
      onMouseLeave={() => {
        setTooltipState((prev) => (prev === "hover" ? "hidden" : prev));
      }}
    >
      <button
        type="button"
        className={className ?? ""}
        aria-label={LABEL_IDLE}
        disabled={isDisabled}
        onClick={(event) => {
          void handleClick(event);
        }}
      >
        <span
          className={`copy-lp-icon-swap ${copied ? "copy-lp-icon-swap--copied" : ""}`}
        >
          <CopyIcon
            className={`copy-lp-icon copy-lp-icon--copy ${iconClassName ?? ""}`}
            aria-hidden="true"
          />
          <CheckIcon
            className={`copy-lp-icon copy-lp-icon--check ${iconClassName ?? ""}`}
            aria-hidden="true"
          />
        </span>
      </button>

      <span
        className={`copy-lp-tooltip ${tooltipVisible ? "copy-lp-tooltip--visible" : ""}`}
        role="tooltip"
      >
        {tooltipText}
      </span>
    </span>
  );
}
